import styled from 'styled-components';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import { useCallback, useContext, useMemo } from 'react';
import { BonusOverviewContext } from '../providers/BonusOverviewProvider';
import Typography from '../../../kingpin/atoms/Typography';
import Row from '../../Common/Row';
import useValueFormatters from '../../../hooks/useValueFormatters';
import getIdentifier from '../../../getIdentifier';
import Icon from '../../../kingpin/atoms/Icon';
import { isArnoldBrosRewardBands } from '../utils/rewardBandsUtils';
import formatRewardRate from '../utils/formatRewardRate';

export const OuterDiv = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: var(--color-tooltip-bg);
  border-radius: 8px;
`;

const ContentBlockDiv = styled.div`
  background-color: var(--color-tooltip-section-bg);
  padding: 16px;
  border-radius: 8px;
`;

const TableTitleDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid var(--color-tooltip-divider);
`;

const RewardBandDiv = styled.div<{ isActive: boolean; isAchieved: boolean }>`
  padding: 4px 8px;
  border-radius: 8px;
  width: 280px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  background-color: ${({ isActive }) =>
    isActive ? 'var(--color-tooltip-row-active-bg)' : 'transparent'};
  opacity: ${({ isActive, isAchieved }) =>
    isActive ? '1' : isAchieved ? '0.35' : '1'};
`;

const RewardBandItem = ({
  band,
  actualValue,
  isFailureBand,
  formatTarget,
}: {
  band: BonusTypes.Rewards.RewardBand;
  actualValue: number;
  isFailureBand: boolean;
  formatTarget: (target: number) => string | number;
}) => {
  const mode = useMemo((): 'active' | 'achieved' | 'unmet' => {
    if (band.active) {
      return 'active';
    }

    const { min, equal, max } = band;
    if (min != null) {
      if (isFailureBand) {
        return actualValue >= min ? 'achieved' : 'unmet';
      }
      return actualValue < min ? 'achieved' : 'unmet';
    }
    if (max != null) {
      if (isFailureBand) {
        return actualValue <= max ? 'achieved' : 'unmet';
      }
      return actualValue > max ? 'achieved' : 'unmet';
    }
    if (equal != null) {
      return actualValue === equal ? 'achieved' : 'unmet';
    }

    return 'unmet';
  }, [actualValue, band, isFailureBand]);

  const iconColor = useMemo(() => {
    if (mode === 'active') {
      return 'var(--color-tooltip-text)';
    }
    return 'var(--color-tooltip-text-muted)';
  }, [mode]);
  const textColor = useMemo(() => {
    if (mode === 'active') {
      return 'var(--color-tooltip-text)';
    }

    return 'var(--color-tooltip-text-muted)';
  }, [mode]);

  const bandOperator = useMemo(() => {
    if (band.min != null) {
      return isFailureBand ? 'Greater than' : '>';
    }
    if (band.max != null) {
      return isFailureBand ? 'Less than' : '<';
    }
    if (band.equal != null) {
      return '=';
    }
  }, [band.equal, band.max, band.min, isFailureBand]);

  const bandTarget = useMemo(() => {
    if (band.min != null) {
      return band.min;
    }
    if (band.max != null) {
      return band.max;
    }
    if (band.equal != null) {
      return band.equal;
    }
  }, [band.equal, band.max, band.min]);

  return (
    <RewardBandDiv
      isActive={mode === 'active'}
      isAchieved={mode === 'achieved'}
    >
      <Row style={{ gap: 4, flex: 2 }} centerAlign>
        <Icon icon={'circleCheckmark'} color={iconColor} />
        <Typography.Body type={'Body 13 Bold'} color={textColor}>
          {bandOperator}
        </Typography.Body>
        <Typography.Body type={'Body 13 Bold'} color={textColor}>
          {bandTarget === undefined ? '' : formatTarget(bandTarget)}
        </Typography.Body>
      </Row>
      <div style={{ flex: 1, textAlign: 'end' }}>
        <Typography.Body type={'Body 13 Bold'} color={textColor}>
          {band.value}
        </Typography.Body>
      </div>
    </RewardBandDiv>
  );
};

// ---------------------------------------------------------------------------
// LEGACY / FROZEN reward shape (config < 3.0.0) — DO NOT REMOVE.
//
// Arnold Bros moved to a MAJOR reward-schema change at config v3.0.0 (Jul 2026);
// the current shape is `multiplierBanded` / `aggregated`, rendered by the v3.x
// blocks further down. It is tempting to treat these component blocks as dead
// code now that the live program is 3.x — they are NOT. A driver's bonus record
// is FROZEN at the config version it was computed under, so every pre-3.0.0
// period keeps returning this `scaledMultiplierBanded` shape (nested
// `rewardBands.components[]`) indefinitely — verified against live data in 2026
// (e.g. May 2026 still serves record versions 2.1.0 / 2.2.0). The leaderboard
// Score tooltip dispatches on the reward shape at runtime
// (BonusScoreCell → BonusScoreBandsTooltip), so both these blocks and the v3.x
// blocks below are load-bearing. See ENG-944.
// ---------------------------------------------------------------------------
export const ArnoldBrosComponentBlock = ({
  component,
  dataset,
}: {
  component: BonusTypes.Rewards.ArnoldBrosRewardBandsComponent;
  dataset: string;
}) => {
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField } = useValueFormatters();

  const fieldLabel = useMemo(
    () => getFieldLabel({ field: component.field, dataType: dataset }),
    [component.field, dataset, getFieldLabel],
  );

  const formatTarget = useCallback(
    (target: number) =>
      formatField({ field: component.field, dataset, value: target }),
    [component.field, dataset, formatField],
  );

  const normalizedBands = useMemo(
    // A simplified current-period reward component may omit `bands`; fall back to
    // an empty ladder (header only) rather than throwing. See ENG-944.
    (): BonusTypes.Rewards.RewardBand[] =>
      (component.bands ?? []).map((b) => ({
        value: b.value,
        active: b.active,
        min: b.min ?? undefined,
        max: b.max ?? undefined,
      })),
    [component.bands],
  );

  const bandForNoReward = useMemo(():
    | BonusTypes.Rewards.RewardBand
    | undefined => {
    if (normalizedBands.length === 0) {
      return undefined;
    }
    const isActive = !normalizedBands.some((b) => b.active);
    const firstBand = normalizedBands[0];
    if (firstBand.min != null) {
      return { max: firstBand.min, value: 0, active: isActive };
    }
    if (firstBand.max != null) {
      return { min: firstBand.max, value: 0, active: isActive };
    }
    return undefined;
  }, [normalizedBands]);

  const actualValue = component.fieldValue ?? 0;

  const formattedActualValue = useMemo(
    () =>
      formatField({
        field: component.field,
        dataset,
        value: component.fieldValue,
      }),
    [component.field, dataset, component.fieldValue, formatField],
  );

  return (
    <>
      <ContentBlockDiv>
        <TableTitleDiv>
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {`${fieldLabel} (${formattedActualValue})`}
          </Typography.Body>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            {'%'}
          </Typography.Body>
        </TableTitleDiv>
        <div style={{ margin: '0px -8px' }}>
          {!!bandForNoReward && (
            <RewardBandItem
              band={bandForNoReward}
              actualValue={actualValue}
              isFailureBand
              formatTarget={formatTarget}
            />
          )}
          {normalizedBands.map((band) => (
            <RewardBandItem
              key={getIdentifier(undefined, true)}
              band={band}
              actualValue={actualValue}
              isFailureBand={false}
              formatTarget={formatTarget}
            />
          ))}
        </div>
      </ContentBlockDiv>
    </>
  );
};

export const ArnoldBrosCombinedBlock = ({
  rewardBands,
  dataset,
}: {
  rewardBands: BonusTypes.Rewards.ArnoldBrosRewardBands;
  dataset: string;
}) => {
  const { getFieldLabel } = useGetFieldLabel();

  return (
    <ContentBlockDiv>
      <TableTitleDiv>
        <Typography.Body
          type={'Body 14 Bold'}
          color={'var(--color-tooltip-text)'}
        >
          Score
        </Typography.Body>
      </TableTitleDiv>
      {rewardBands.components.map((component, index) => (
        <Row
          key={component.field}
          spaceBetween
          centerAlign
          style={{ marginBottom: 4 }}
        >
          <Typography.Body type={'Body 13'} color={'var(--color-tooltip-text)'}>
            {getFieldLabel({ field: component.field, dataType: dataset })}
          </Typography.Body>
          <Typography.Body
            type={'Body 13 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {index > 0
              ? `${rewardBands.combineMethod === 'multiply' ? '×' : '+'} ${component.percentage}%`
              : `${component.percentage}%`}
          </Typography.Body>
        </Row>
      ))}
      <Row
        spaceBetween
        centerAlign
        style={{
          borderTop: '1px solid var(--color-tooltip-divider)',
          paddingTop: 8,
          marginTop: 4,
        }}
      >
        <Typography.Body
          type={'Body 14 Bold'}
          color={'var(--color-tooltip-text)'}
        >
          {rewardBands.combineMethod === 'multiply' ? 'Combined' : 'Average'}
        </Typography.Body>
        <Typography.Body
          type={'Body 14 Bold'}
          color={'var(--color-tooltip-text)'}
        >
          {`${rewardBands.combinedPercentage}%`}
        </Typography.Body>
      </Row>
    </ContentBlockDiv>
  );
};

// ---------------------------------------------------------------------------
// v3.x (config 3.0.0+) score reward rendering.
//
// The Arnold Bros program moved to a MAJOR schema change: `safety`/`operations`
// scores are now `multiplierBanded` (a flat `rewardBands.bands[]`, no nested
// `components`), and `fuel` is `aggregated` (children combined via
// `scoreCombine`). Each band carries both a `score` tier and a `value` ($/unit
// rate). These blocks are dataset-prop-based (no BonusOverviewContext), so they
// work in the leaderboard cell. Frozen pre-3.0.0 periods still use the
// component-based blocks above (kept deliberately — see the LEGACY / FROZEN note
// there); the tooltip dispatches on the reward shape at runtime. See ENG-944.
// ---------------------------------------------------------------------------

const getRateUnits = (multiplierField?: string): string | undefined =>
  multiplierField?.toLowerCase().includes('miles') ? 'miles' : undefined;

// Wider than the 2-column `RewardBandDiv` — the v3.x ladder has three columns
// (threshold | score | $/mile rate) and the rate ("0.25¢ / mile") must not wrap.
const MultiplierBandDiv = styled.div<{
  isActive: boolean;
  isAchieved: boolean;
}>`
  padding: 4px 8px;
  border-radius: 8px;
  width: 400px;
  display: flex;
  flex-direction: row;
  align-items: center;

  background-color: ${({ isActive }) =>
    isActive ? 'var(--color-tooltip-row-active-bg)' : 'transparent'};
  opacity: ${({ isActive, isAchieved }) =>
    isActive ? '1' : isAchieved ? '0.35' : '1'};
`;

const MultiplierBandRow = ({
  band,
  actualValue,
  formatTarget,
  rateLabel,
}: {
  band: BonusTypes.Rewards.RewardBand;
  actualValue: number;
  formatTarget: (target: number) => string | number;
  rateLabel: string;
}) => {
  const mode = useMemo((): 'active' | 'achieved' | 'unmet' => {
    if (band.active) {
      return 'active';
    }
    const { min, max, equal } = band;
    if (min != null) {
      return actualValue < min ? 'achieved' : 'unmet';
    }
    if (max != null) {
      return actualValue > max ? 'achieved' : 'unmet';
    }
    if (equal != null) {
      return actualValue === equal ? 'achieved' : 'unmet';
    }
    return 'unmet';
  }, [actualValue, band]);

  const textColor =
    mode === 'active'
      ? 'var(--color-tooltip-text)'
      : 'var(--color-tooltip-text-muted)';

  const { bandOperator, bandTarget } = useMemo(() => {
    if (band.min != null) {
      return { bandOperator: '>', bandTarget: band.min };
    }
    if (band.max != null) {
      return { bandOperator: '<', bandTarget: band.max };
    }
    if (band.equal != null) {
      return { bandOperator: '=', bandTarget: band.equal };
    }
    return { bandOperator: undefined, bandTarget: undefined };
  }, [band.equal, band.max, band.min]);

  const score = band.score ?? band.value;

  return (
    <MultiplierBandDiv
      isActive={mode === 'active'}
      isAchieved={mode === 'achieved'}
    >
      <Row style={{ gap: 4, flex: 2 }} centerAlign>
        <Icon icon={'circleCheckmark'} color={textColor} />
        <Typography.Body type={'Body 13 Bold'} color={textColor}>
          {bandOperator}
        </Typography.Body>
        <Typography.Body type={'Body 13 Bold'} color={textColor}>
          {bandTarget === undefined ? '' : formatTarget(bandTarget)}
        </Typography.Body>
      </Row>
      <div style={{ flex: 1, textAlign: 'center' }}>
        <Typography.Body type={'Body 13 Bold'} color={textColor}>
          {score}
        </Typography.Body>
      </div>
      <div style={{ flex: 1, textAlign: 'end', whiteSpace: 'nowrap' }}>
        <Typography.Body type={'Body 13 Bold'} color={textColor}>
          {rateLabel}
        </Typography.Body>
      </div>
    </MultiplierBandDiv>
  );
};

export const MultiplierBandedBlock = ({
  reward,
  dataset,
  rewardType,
}: {
  reward: BonusTypes.Rewards.RewardOutput;
  dataset: string;
  rewardType: 'none' | 'points' | 'monetary';
}) => {
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField } = useValueFormatters();

  const rewardBands = reward.rewardBands;
  const field = rewardBands?.field ?? reward.name;

  const fieldLabel = useMemo(
    () => getFieldLabel({ field, dataType: dataset }),
    [field, dataset, getFieldLabel],
  );

  const actualValue = rewardBands?.fieldValue ?? 0;

  const formattedActualValue = useMemo(
    () => formatField({ field, dataset, value: rewardBands?.fieldValue }),
    [field, dataset, rewardBands?.fieldValue, formatField],
  );

  const formatTarget = useCallback(
    (target: number) => formatField({ field, dataset, value: target }),
    [field, dataset, formatField],
  );

  const units = getRateUnits(rewardBands?.multiplierField);
  const bands = rewardBands?.bands ?? [];

  if (bands.length === 0) {
    return null;
  }

  return (
    <ContentBlockDiv>
      <TableTitleDiv>
        <div style={{ flex: 2 }}>
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {`${fieldLabel} (${formattedActualValue})`}
          </Typography.Body>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            {'Score'}
          </Typography.Body>
        </div>
        <div style={{ flex: 1, textAlign: 'end' }}>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            {'Rate'}
          </Typography.Body>
        </div>
      </TableTitleDiv>
      <div style={{ margin: '0px -8px' }}>
        {bands.map((band) => (
          <MultiplierBandRow
            key={getIdentifier(undefined, true)}
            band={band}
            actualValue={actualValue}
            formatTarget={formatTarget}
            rateLabel={formatRewardRate({
              rate: band.value,
              rewardType,
              units,
            })}
          />
        ))}
      </div>
    </ContentBlockDiv>
  );
};

export const AggregatedRewardBandsBlock = ({
  reward,
  dataset,
  rewardType,
}: {
  reward: BonusTypes.Rewards.RewardOutput;
  dataset: string;
  rewardType: 'none' | 'points' | 'monetary';
}) => {
  const { getFieldLabel } = useGetFieldLabel();

  const children = reward.rewards ?? [];
  const combineLabel = reward.scoreCombine === 'avg' ? 'Average' : 'Combined';

  return (
    <>
      <ContentBlockDiv>
        <TableTitleDiv>
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            Score
          </Typography.Body>
        </TableTitleDiv>
        {children.map((child) => (
          <Row
            key={child.name}
            spaceBetween
            centerAlign
            style={{ marginBottom: 4 }}
          >
            <Typography.Body
              type={'Body 13'}
              color={'var(--color-tooltip-text)'}
            >
              {getFieldLabel({
                field: child.rewardBands?.field ?? child.name,
                dataType: dataset,
              })}
            </Typography.Body>
            <Typography.Body
              type={'Body 13 Bold'}
              color={'var(--color-tooltip-text)'}
            >
              {child.score ?? ''}
            </Typography.Body>
          </Row>
        ))}
        <Row
          spaceBetween
          centerAlign
          style={{
            borderTop: '1px solid var(--color-tooltip-divider)',
            paddingTop: 8,
            marginTop: 4,
          }}
        >
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {combineLabel}
          </Typography.Body>
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {reward.score ?? ''}
          </Typography.Body>
        </Row>
      </ContentBlockDiv>
      {children.map((child) => (
        <MultiplierBandedBlock
          key={child.name}
          reward={child}
          dataset={dataset}
          rewardType={rewardType}
        />
      ))}
    </>
  );
};

const RewardBandsTooltip = ({
  rewardBands,
}: {
  rewardBands:
    | BonusTypes.Rewards.RewardBands
    | BonusTypes.Rewards.ArnoldBrosRewardBands;
}) => {
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { getFieldLabel } = useGetFieldLabel();
  const { formatField } = useValueFormatters();

  if (isArnoldBrosRewardBands(rewardBands)) {
    if (!bonusOverview) {
      return null;
    }
    return (
      <OuterDiv>
        {rewardBands.components.length > 1 && (
          <ArnoldBrosCombinedBlock
            rewardBands={rewardBands}
            dataset={bonusOverview.bonusProgram.dataset}
          />
        )}
        {rewardBands.components.map((component) => (
          <ArnoldBrosComponentBlock
            key={component.field}
            component={component}
            dataset={bonusOverview.bonusProgram.dataset}
          />
        ))}
      </OuterDiv>
    );
  }

  const fieldLabel = (() => {
    if (!bonusOverview) {
      console.warn('RewardBandsTooltip: BonusOverview is undefined');
      return undefined;
    }

    return getFieldLabel({
      field: rewardBands.field,
      dataType: bonusOverview.bonusProgram.dataset,
    });
  })();

  const actualValue = rewardBands.fieldValue;

  const formattedActualValue = (() => {
    if (!bonusOverview) {
      console.warn('RewardBandsTooltip: BonusOverview is undefined');
      return undefined;
    }

    return formatField({
      field: rewardBands.field,
      dataset: bonusOverview.bonusProgram.dataset,
      value: actualValue,
    });
  })();

  const formatTarget = (target: number) => {
    if (!bonusOverview) {
      console.warn('RewardBandsTooltip: BonusOverview is undefined');
      return target;
    }

    return formatField({
      field: rewardBands.field,
      dataset: bonusOverview.bonusProgram.dataset,
      value: target,
    });
  };

  const bandForNoReward = ((): BonusTypes.Rewards.RewardBand | undefined => {
    if (rewardBands.bands.length === 0) {
      return undefined;
    }
    const isActive = !rewardBands.bands.some((b) => b.active);

    const firstBand = rewardBands.bands[0];
    if (firstBand.min != null) {
      return { max: firstBand.min, value: 0, active: isActive };
    }
    if (firstBand.max != null) {
      return { min: firstBand.max, value: 0, active: isActive };
    }

    return undefined;
  })();

  const isDollarsReward = bonusOverview?.bonusProgram.rewardType === 'monetary';

  return (
    <OuterDiv>
      <ContentBlockDiv>
        <Row spaceBetween centerAlign>
          <Typography.Body
            type={'Body 14 Bold'}
            color={'var(--color-tooltip-text)'}
          >
            {fieldLabel}
          </Typography.Body>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            {formattedActualValue}
          </Typography.Body>
        </Row>
      </ContentBlockDiv>
      <ContentBlockDiv>
        <TableTitleDiv>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            {fieldLabel}
          </Typography.Body>
          <Typography.Body type={'Body 14'} color={'var(--color-tooltip-text)'}>
            {isDollarsReward ? '$ Per Mile' : 'Points'}
          </Typography.Body>
        </TableTitleDiv>
        <div style={{ margin: '0px -8px' }}>
          {!!bandForNoReward && (
            <RewardBandItem
              band={bandForNoReward}
              actualValue={actualValue || 0}
              isFailureBand
              formatTarget={formatTarget}
            />
          )}
          {rewardBands.bands.map((band) => (
            <RewardBandItem
              key={getIdentifier(undefined, true)}
              band={band}
              actualValue={actualValue || 0}
              isFailureBand={false}
              formatTarget={formatTarget}
            />
          ))}
        </div>
      </ContentBlockDiv>
    </OuterDiv>
  );
};

export default RewardBandsTooltip;
